import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/auth';
import Swal from 'sweetalert2';
import { getEmployeeContractsList } from '../services/api_contracts_employee';
import { getWeeklyWorkingHourCompound, createEmployeeWeeklyWorkingHour, getEmployeeWeeklyWorkingHour, updateEmployeeWeeklyWorkingHour, deleteEmployeeWeeklyWorkingHour } from '../services/api_working_hours';
import ComboboxAutocomplete from './combobox_autocomplete';
import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'wouter';



const TurnoTabEmployee = ({ employeeId, setUnsavedChanges, employee, employeeEmailAuth, onTabContractChange }) => {
    const { token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const daysOfWeek = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica']; 
    const [contracts, setContracts] = useState([]);
    const [employeeContract, setEmployeeContract] = useState(null);
    const [shiftType, setShiftType] = useState('');
    const [weeklyWorkingHours, setWeeklyWorkingHours] = useState([]);
    const [selectedWorkingHour, setSelectedWorkingHour] = useState(null);
    const [wwhEmployee, setWwhEmployee] = useState(null);
    const { codiceFiscale } = useParams();  

    const wwhEmployeeData = weeklyWorkingHours.find(wwh => wwh.id === wwhEmployee);
    
    const hasManageShiftsPermission = profile?.customer_type === 'employee' && (
        profile?.employee?.permissions.includes('hr.manage_shifts_all') || 
        profile?.employee?.permissions.includes('hr.manage_shifts_ou')
    );
    
    useEffect(() => {
        if (!employeeId) {
            return;
        }
        getContractList();
    }, [employeeId]);

    const getContractList = () => {
        setIsLoading(true);
        setError(null);

        getEmployeeContractsList(token, employeeId, 'payrolls')
            .then(data => {
                setContracts(data);

                const activeContract = data.find(contract => contract.is_active);
                if (activeContract) {
                    setEmployeeContract(activeContract);
                    setShiftType(activeContract.shift_type || '');
                } else {
                    setEmployeeContract(null);
                    setShiftType('');
                }
            })
            .catch(error => {
                setError(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const fetchWeekWorkingHours = async () => {
            try {
                const weeklyWorkingHours = await getWeeklyWorkingHourCompound(token, '', employeeId);
                setWeeklyWorkingHours(weeklyWorkingHours);
            } catch (error) {
                console.error('Errore durante il recupero del turno', error);
            }
        };

        fetchWeekWorkingHours();
    }, [token]);

    useEffect(() => {
        const fetchEmployeeWeeklyWorkingHourData = async () => {
          try {
           
            const employeeWeeklyWorkingHourData = await getEmployeeWeeklyWorkingHour(token, employeeId);
            if (employeeWeeklyWorkingHourData && employeeWeeklyWorkingHourData.weekly_working_hour) {
                setWwhEmployee(employeeWeeklyWorkingHourData.weekly_working_hour);
              }
  
          } catch (error) {
            console.error('Errore durante il recupero dell\'orario lavorativo', error);
          }
        };
      
        fetchEmployeeWeeklyWorkingHourData();
    }, [token, employeeId]);
      

    const handleSelectWorkingHour = (selected) => {
        setSelectedWorkingHour(selected);
        setUnsavedChanges(true);
    };

    const handleSaveEmployeeWWH = async () => {
        if (!selectedWorkingHour) return;
    
        let weekHoursEmployee = employeeContract.week_hours * 60;
    
        const saveAndNotify = async () => {
            try {
                await saveWeeklyWorkingHour();
                setUnsavedChanges(false);
                
                Swal.fire({
                    title: 'Turno settimanale assegnato con successo!',
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1500,
                });
    
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: error.message || 'Errore sconosciuto',
                });
            }
        };
        
        if (weekHoursEmployee !== selectedWorkingHour.duration) {
            const result = await Swal.fire({
                title: 'Confermi?',
                html: `L'orario settimanale del dipendente è di <b>${formatMinutesToHours(weekHoursEmployee)}</b> e stai assegnando un orario settimanale di <b>${formatMinutesToHours(selectedWorkingHour.duration)}</b>. Confermi l'operazione?`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Conferma',
                cancelButtonText: 'Annulla',
                cancelButtonColor: '#d1d5db',
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
    
            if (!result.isConfirmed) return;
        }

        await saveAndNotify();
    };
       
    
    const saveWeeklyWorkingHour = async () => {
        const wwhId = selectedWorkingHour.id;
    
        if (typeof wwhId !== 'number') {
            throw new Error('ID del turno settimanale non valido');
        }
    
        const data = {
            weekly_working_hour: wwhId
        };
    
        const response = !wwhEmployee
            ? await createEmployeeWeeklyWorkingHour(token, employeeId, data)
            : await updateEmployeeWeeklyWorkingHour(token, employeeId, data);
    
        setSelectedWorkingHour(null);
        setWwhEmployee(wwhId);
    };    
    
    const handleCancelSave = () => {
        setSelectedWorkingHour(null);
        setUnsavedChanges(false);
    }


    const handleDeleteEmployeeWWH = async () => {
        Swal.fire({
            title: 'Sei sicuro di voler eliminare l\'assegnazione del turno?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (!wwhEmployeeData || !wwhEmployeeData.id) {
                        throw new Error('Nessun turno da eliminare');
                    }

                    const refOu = wwhEmployeeData.ou || null;
    
                    await deleteEmployeeWeeklyWorkingHour(token, employeeId);
                    setWwhEmployee(null);
    
                    await Swal.fire({
                        title: 'Turno eliminato con successo!',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    }); 
                } catch (error) {
                    Swal.fire({
                        title: 'Errore durante l\'eliminazione del turno settimanale',
                        text: error.message || 'Errore sconosciuto',
                        icon: 'error',
                    });
                }
            }
        });
    };  

    const formatMinutesToHours = (minutes) => {
    const totalHours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (totalHours === 0 && remainingMinutes === 0) {
        return '0 min';
    } else if (totalHours === 0) {
        return `${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
        return `${totalHours} ore`;
    } else {
        return `${totalHours} ore, ${remainingMinutes} min`;
    }
    };

    if(isLoading)
        return <div className="relative block w-full rounded-lg  p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2">
            <div role="status">
                <svg className="inline w-10 h-10 mr-2 text-gray-200 animate-spin dark:text-grey-100 fill-indigo-700" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span className="sr-only">Loading...</span>
            </div>
            <span className="mt-2 block text-sm font-semibold text-gray-900">Caricamento...</span>
        </div>
    if (error) return <div>Error: {error.message}</div>;

    const wwhEmployeeDataDisplay = selectedWorkingHour || wwhEmployeeData;

    return (
        <div className='container px-4 w-full overflow-y-auto' style={{ height: '50vh'}}>
            {employeeContract ? (
                <div className='mx-4 mb-10'>
                    {employeeContract.week_hours ? (
                        <>
                            {shiftType === 'fisso' ? (
                                <>  
                                    <div className='grid grid-cols-3 pb-4'>
                                        <div className='col-span-1 flex items-center'>
                                            <div>
                                                <h3 className='block text-sm font-medium text-gray-700'>Seleziona il turno da assegnare</h3>
                                            </div>

                                        </div>
                                        <div className='col-span-2 items-center'>
                                            <ComboboxAutocomplete
                                                options={weeklyWorkingHours}
                                                bgActiveColor="bg-indigo-100"
                                                textActiveColor="text-gray-900"
                                                placeholder='Cerca il turno con nome...'
                                                onChange={handleSelectWorkingHour}
                                                value={selectedWorkingHour}
                                                positionOptions='absolute'
                                            />
                                        </div>
                                    </div>

                                    {(!weeklyWorkingHours || weeklyWorkingHours.length === 0) && (
                                        <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                                            <div className="text-center p-5">
                                                <p>Nessun turno settimanale presente da assegnare</p>
                                                <p>Vai alla pagina di <Link to="/shift_management?type=settimanale"><span className='underline text-indigo-700 uppercase cursor-pointer'>creazione turno</span></Link></p>
                                            </div>
                                        </div>
                                    )}

                                    {(wwhEmployeeData && !selectedWorkingHour) && (
                                        <div className='pt-6 grid grid-cols-1 sm:grid-cols-2 justify-around'>
                                            <div className='col-span-1'>
                                                <h3 className='block text-sm font-medium text-gray-700'>Turno assegnato: <span className='uppercase font-semibold text-indigo-700'>{wwhEmployeeData?.name}</span></h3>
                                            </div>
                                            <div className='col-span-1 sm:text-end text-sm font-medium text-gray-700'>
                                            Totale Ore Settimanali: {wwhEmployeeData?.duration !== undefined ? formatMinutesToHours(wwhEmployeeData.duration) : 'Non disponibile'}
                                            </div>
                                            
                                        </div>
                                    )}
                                    
                                    {wwhEmployeeDataDisplay && (
                                        <div className='mt-4 overflow-x-auto'>
                                            {(selectedWorkingHour && selectedWorkingHour.duration !== null) && (
                                                <div className='flex justify-start text-sm font-medium text-gray-700 pb-4'>
                                                    Totale Ore Settimanali: {formatMinutesToHours(selectedWorkingHour.duration)}
                                                </div>
                                            )}
                                            <table className="w-full rounded-md border-collapse overflow-auto" style={{ maxWidth: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        {daysOfWeek.slice(0, 7).map((day, index) => ( 
                                                            <th key={index} className="px-4 py-2 text-sm font-bold text-center whitespace-nowrap text-gray-500 border border-gray-300 rounded-md" style={{ width: `${100 / 7}%` }}>
                                                                {day}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="px-1 py-2 text-sm text-center whitespace-nowrap text-gray-700">
                                                        {daysOfWeek.map((day, columnIndex) => (
                                                            <td key={columnIndex} className="px-4 py-2 text-sm text-center whitespace-nowrap text-gray-700 align-top border border-gray-300 rounded-md" style={{ width: `${100 / 7}%` }}>
                                                                {(wwhEmployeeDataDisplay.slots_by_weekday || []).flatMap((weekDay) => {
                                                                    if (weekDay.week_day - 1 === columnIndex) {
                                                                        const timeSlots = weekDay.working_hour?.time_slots || [];
                                                                        const additionalTimeSlots = weekDay.time_slots || [];
                                                                        const allTimeSlots = [...timeSlots, ...additionalTimeSlots];

                                                                        allTimeSlots.sort((a, b) => {
                                                                            const timeA = new Date(`1970-01-01T${a.start_at}`);
                                                                            const timeB = new Date(`1970-01-01T${b.start_at}`);
                                                                            return timeA.getTime() - timeB.getTime();
                                                                        });

                                                                        return allTimeSlots;
                                                                    }
                                                                    return [];
                                                                }).map((timeSlot, timeSlotIndex) => (
                                                                    <div key={`${columnIndex}-${timeSlotIndex}`} className="mt-2 mb-5">
                                                                        <BriefcaseIcon className='h-5 w-5 inline-block' />
                                                                        <span 
                                                                            className="font-bold underline-offset-4" 
                                                                            style={{
                                                                                textDecoration: 'underline',
                                                                                textDecorationColor: '#2b6cb0',
                                                                                textDecorationThickness: '1px',
                                                                        }}>
                                                                            {timeSlot.start_at} - {timeSlot.end_at}
                                                                        </span>
                                                                        {timeSlot.breaks.map((breakItem, breakIndex) => (
                                                                            <div key={breakIndex} className='my-1'>
                                                                                <span className='inline-block w-5 h-5 pt-2 me-1'><SvgBreak /></span> 
                                                                                {`${breakItem.start_at} - ${breakItem.end_at}`}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {(selectedWorkingHour && selectedWorkingHour !== wwhEmployeeData) ? (
                                                <div className='col-span-3 my-4 mx-8 flex justify-center'>
                                                    {(profile.customer_type !== 'employee' || hasManageShiftsPermission) ? (
                                                        <>
                                                            <button 
                                                                className='bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4 w-24'
                                                                onClick={handleSaveEmployeeWWH}
                                                            >
                                                                Salva
                                                            </button>
                                                            <button 
                                                                className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-24'
                                                                onClick={handleCancelSave}
                                                            >
                                                                Annulla
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className='text-center block text-md font-medium text-indigo-600'>
                                                            Non hai i permessi per modificare il turno
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className='col-span-2 my-4 mx-8 flex justify-center'>
                                                    {(profile.customer_type !== 'employee' || hasManageShiftsPermission) ? (
                                                        <button 
                                                            className='bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline mr-4 w-24'
                                                            onClick={handleDeleteEmployeeWWH}
                                                        >
                                                            Elimina
                                                        </button>
                                                    ) : (
                                                        <div className='text-center block text-md font-medium text-indigo-600'>
                                                            Non hai i permessi per modificare il turno
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : shiftType === 'senza_calendario' ? (
                                <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'>
                                    <div className="text-center p-5">
                                        <p>La tipologia di contratto del dipendente è "senza calendario".</p>
                                        <p>Non è possibile assegnare il turno di lavoro</p>
                                    </div>
                                </div>
                            ) : (
                                <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                                    <div className="text-center p-5">
                                        <p>Il turno del dipendente può essere assegnato sul calendario</p>
                                        <p>Vai alla pagina di <Link to="/assign_working_hour"><span className='underline text-indigo-700 uppercase cursor-pointer'>assegnazione turno</span></Link></p>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='col-span-5 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                            <div className="text-center p-5">
                                <p>L'orario di lavoro non è presente sull'attuale contratto attivo e non è possibile assegnare un turno.</p>
                                <p>
                                    Vai alla tab di{' '}
                                    <span
                                        className="underline text-indigo-700 uppercase cursor-pointer"
                                        onClick={() => {
                                            onTabContractChange(2);
                                            window.location.href = `/dipendenti/${codiceFiscale}/edit?tab=2`;
                                        }}
                                    >
                                        modifica contratto
                                    </span>{' '}
                                    per salvare l'orario settimanale.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="m-8 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-semibold text-gray-900">
                    <div className="text-center p-5">
                        <p>Non sono presenti contratti attivi</p>
                    </div>
                </div>
            )}
        </div> 
    );
};

export default TurnoTabEmployee;


const SvgBreak = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 117 122.88" style={{ width: '15px' }}>
            <g>
                <path d="M94.8,15.36c-1.18-0.34-1.86-1.56-1.53-2.74c0.34-1.18,1.56-1.86,2.74-1.53c3.15,0.9,5.79,2.39,7.82,4.59 c2.03,2.22,3.39,5.1,3.94,8.79c0.18,1.21-0.66,2.34-1.88,2.52c-1.21,0.18-2.34-0.66-2.52-1.88c-0.41-2.76-1.38-4.87-2.81-6.43 C99.13,17.12,97.17,16.04,94.8,15.36L94.8,15.36z M37.87,67.17L0.61,28.09c-0.84-0.89-0.81-2.29,0.08-3.13 c0.43-0.41,0.98-0.61,1.53-0.61v-0.01h14.53L2.27,9.86c-0.87-0.87-0.87-2.27,0-3.14c0.87-0.87,2.27-0.87,3.14,0l17.62,17.62h42.2 c0.46-6.48,3.24-12.31,7.52-16.64C77.44,2.94,83.92,0,91.08,0c7.16,0,13.64,2.94,18.34,7.7C114.1,12.45,117,19,117,26.23 c0,7.23-2.9,13.78-7.58,18.53c-4.7,4.76-11.18,7.7-18.34,7.7c-3.19,0-6.25-0.59-9.09-1.66c-2.33-0.88-4.51-2.1-6.48-3.6L56,67.18 v38.38l14.94,13.45c0.91,0.82,0.98,2.22,0.16,3.13c-0.44,0.49-1.04,0.73-1.65,0.73v0H24.77c-1.23,0-2.22-0.99-2.22-2.22 c0-0.7,0.32-1.32,0.82-1.73l14.5-13.36V67.17L37.87,67.17z M69.68,24.34h22.88c1.23,0,2.22,0.99,2.22,2.22 c0,0.66-0.29,1.26-0.75,1.67L78.64,44c1.5,1.08,3.15,1.98,4.92,2.65c2.33,0.88,4.87,1.37,7.52,1.37c5.93,0,11.3-2.43,15.18-6.36 c3.89-3.94,6.3-9.39,6.3-15.42c0-6.03-2.41-11.48-6.3-15.42c-3.88-3.93-9.25-6.36-15.18-6.36c-5.93,0-11.3,2.43-15.18,6.36 C72.42,14.33,70.13,19.06,69.68,24.34L69.68,24.34z M7.4,28.78l6.82,7.15c0.14-0.03,0.29-0.05,0.45-0.05h64.77 c0.28,0,0.54,0.05,0.79,0.14l7.08-7.25H7.4L7.4,28.78z M18.41,40.33l23.18,24.31c0.45,0.41,0.73,0.99,0.73,1.65v40.26h0 c0,0.6-0.24,1.2-0.72,1.63l-11.14,10.26h33.22l-11.29-10.16c-0.5-0.41-0.83-1.03-0.83-1.73V66.28h0.01c0-0.56,0.21-1.11,0.63-1.55 l23.83-24.41H18.41L18.41,40.33z"/>
            </g>
        </svg>
    );
  };
    
